import { useState, useEffect, useCallback } from 'react';
import { useGameStore } from '../stores/gameStore';

/**
 * useDashboard - Custom hook to connect dashboard components to the game engine
 * 
 * @param {Object} initialUserStats - Initial user statistics
 * @returns {Object} - Dashboard data and functions connected to the game engine
 */
export const useDashboard = (initialUserStats = {}) => {
  // Local state for dashboard data
  const [userStats, setUserStats] = useState(initialUserStats);
  const [chartTimeframe, setChartTimeframe] = useState('24h');
  
  // Connect to game store
  const gameState = useGameStore(state => state.gameState);
  const score = useGameStore(state => state.score);
  const player = useGameStore(state => state.player);
  const dashboard = useGameStore(state => state.dashboard);
  const getStoreTimeframeData = useGameStore(state => state.getTimeframeData);
  const updateDashboard = useGameStore(state => state.updateDashboard);
  const addActivity = useGameStore(state => state.addActivity);
  
  // Sync game state with dashboard state
  useEffect(() => {
    // Update userStats based on player data from the game store
    if (player && player.coins !== undefined) {
      setUserStats(prevStats => ({
        ...prevStats,
        balance: {
          ...prevStats.balance,
          jinu: player.coins
        }
      }));
    }
    
    // Sync other dashboard data
    if (dashboard) {
      setUserStats(prevStats => ({
        ...prevStats,
        solContributed: dashboard.userSolContributed,
        totalReferrals: dashboard.totalReferrals,
        totalEarnings: dashboard.totalEarnings,
        daoPower: dashboard.daoPower
      }));
    }
  }, [player, dashboard]);
  
  // Function to get chart data based on timeframe
  const getTimeframeData = useCallback(() => {
    const timeframeData = getStoreTimeframeData(chartTimeframe);
    
    if (!timeframeData || timeframeData.length === 0) {
      return {
        labels: [],
        datasets: []
      };
    }
    
    // Transform the data for the chart component
    const labels = timeframeData.map(item => {
      const date = new Date(item.time);
      return chartTimeframe === '24h' 
        ? `${date.getHours()}:00` 
        : `${date.getMonth() + 1}/${date.getDate()}`;
    });
    
    return {
      labels,
      datasets: [
        {
          label: 'Rank (SOL)',
          data: timeframeData.map(item => item.userSolContributed),
          borderColor: '#00E5FF',
          backgroundColor: 'rgba(0, 229, 255, 0.1)',
          tension: 0.4
        },
        {
          label: 'Affiliates',
          data: timeframeData.map(item => item.totalReferrals),
          borderColor: '#00FF85',
          backgroundColor: 'rgba(0, 255, 133, 0.1)',
          tension: 0.4
        },
        {
          label: 'DAO Power',
          data: timeframeData.map(item => item.daoPower),
          borderColor: '#FF00E5',
          backgroundColor: 'rgba(255, 0, 229, 0.1)',
          tension: 0.4
        },
        {
          label: 'Jackpot',
          data: timeframeData.map(item => item.progressiveJackpot),
          borderColor: '#E5FF00',
          backgroundColor: 'rgba(229, 255, 0, 0.1)',
          tension: 0.4
        }
      ]
    };
  }, [chartTimeframe, getStoreTimeframeData]);
  
  return {
    // Dashboard state
    userStats,
    setUserStats,
    chartTimeframe,
    setChartTimeframe,
    getTimeframeData,
    recentActivity: dashboard.recentActivity,
    addActivity,
    updateDashboard,
    
    // Game engine connection
    gameState,
    score
  };
};

export default useDashboard;
